// 80mm receipt renderer for trade sales (after-sale + reprints).
//
// Desktop parity note: the desktop prints a 40-column ESC/POS text receipt
// (GenericKedaiRuncitApp_GenericPrintReceipt) when a thermal printer is
// configured, and a server PDF for credit sales. In the browser we render a
// printable HTML receipt (any printer incl. thermal via browser print) and
// keep the server PDF (GetInvoice) available as a download for invoices.

import type { TradeSalesRow } from './api.js';

export interface ReceiptOpts {
  /** Wholesale invoices print as INVOICE, cash as RECEIPT. */
  wholesale?: boolean;
  /** Cash tendered + change (payment modal passes them through). */
  tendered?: number;
  change?: number;
}

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) =>
    (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));

const money = (n: number | undefined): string => (Number(n) || 0).toFixed(2);

function fmtQty(qty: number | undefined, packDesc?: string): string {
  const n = Number(qty) || 0;
  // formatqoh-style: drop trailing zeros, keep up to 3 decimals
  const s = n.toFixed(3).replace(/\.?0+$/, '');
  return packDesc ? `${s} ${esc(packDesc)}` : s;
}

function lineRows(sale: TradeSalesRow): string {
  const lines = sale.lines ?? [];
  return lines
    .map((l) => {
      const pack = l.packDesc || l.pack || '';
      const qty = fmtQty(l.quantity, undefined);
      const bonus = l.bonusQty ? ` +${Number(l.bonusQty)}` : '';
      const disc = Number(l.discount) || 0;
      return `
        <tr><td colspan="2" class="item">${esc(l.name)}</td></tr>
        <tr>
          <td class="qr">${qty}${bonus}${pack ? ' <span class="pack">' + esc(pack) + '</span>' : ''} @ ${money(l.price)}</td>
          <td class="amt">${money(l.value)}</td>
        </tr>
        ${disc ? `<tr><td class="small">Discount</td><td class="amt small">-${money(disc)}</td></tr>` : ''}`;
    })
    .join('');
}

function paymentRows(sale: TradeSalesRow): string {
  const pays = sale.payments ?? [];
  return pays
    .map(
      (p) => `
        <tr><td>${esc(p.paidByName || p.paidByCode || 'Payment')}</td><td class="amt">${money(p.amount)}</td></tr>
        ${p.tendered && p.tendered > p.amount
          ? `<tr><td class="small">Tendered</td><td class="amt small">${money(p.tendered)}</td></tr>`
          : ''}`,
    )
    .join('');
}

/** Render the full printable HTML document for a sale. */
export function renderReceiptHtml(sale: TradeSalesRow, storeName: string, opts: ReceiptOpts = {}): string {
  const d = new Date(sale.date || Date.now());
  const pad = (n: number) => String(n).padStart(2, '0');
  const dateStr =
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`;

  const title = opts.wholesale ? 'INVOICE' : 'RECEIPT';
  const net = sale.total;
  const tax = Number(sale.tax) || 0;
  const rounding = Number(sale.rounding) || 0;
  const discAtTotal = Number(sale.discountAtTotal) || 0;
  const due = net + tax + rounding + discAtTotal;

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>${title} ${esc(sale.seqNo || sale.id)}</title>
<style>
  @page { size: 80mm auto; margin: 2mm; }
  * { box-sizing: border-box; }
  body { width: 72mm; margin: 0 auto; color: #000; background: #fff;
         font: 12px/1.4 "Courier New", ui-monospace, monospace; }
  h1 { font-size: 15px; text-align: center; margin: 0 0 2px; }
  .sub { text-align: center; font-size: 11px; margin-bottom: 6px; }
  .meta { font-size: 11px; margin-bottom: 6px; }
  .meta div { display: flex; justify-content: space-between; gap: 8px; }
  hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  td { padding: 1px 0; vertical-align: top; }
  td.amt { text-align: right; white-space: nowrap; padding-left: 6px; }
  td.item { font-weight: 600; padding-top: 4px; }
  td.qr { font-size: 11px; padding-left: 8px; }
  .pack { font-size: 10px; }
  .small { font-size: 10px; color: #333; }
  .totals td { padding-top: 2px; }
  .totals .grand { font-weight: 700; font-size: 13px; }
  .footer { text-align: center; font-size: 10px; margin-top: 8px; }
  @media print { body { width: auto; } }
</style></head>
<body>
  <h1>${esc(storeName || '')}</h1>
  <div class="sub">${title}</div>
  <div class="meta">
    <div><span>No.</span><span>${esc(sale.seqNo ? '#' + sale.seqNo : sale.id)}</span></div>
    <div><span>Date</span><span>${dateStr}</span></div>
    ${sale.customerName ? `<div><span>Customer</span><span>${esc(sale.customerName)}</span></div>` : ''}
    ${sale.refNo ? `<div><span>Ref</span><span>${esc(sale.refNo)}</span></div>` : ''}
    ${sale.userName ? `<div><span>Cashier</span><span>${esc(sale.userName)}</span></div>` : ''}
  </div>
  <hr>
  <table>${lineRows(sale)}</table>
  <hr>
  <table class="totals">
    <tr><td>Subtotal</td><td class="amt">${money(net)}</td></tr>
    ${tax ? `<tr><td>Tax</td><td class="amt">${money(tax)}</td></tr>` : ''}
    ${rounding ? `<tr><td>Rounding</td><td class="amt">${money(rounding)}</td></tr>` : ''}
    ${discAtTotal ? `<tr><td>Discount</td><td class="amt">${money(discAtTotal)}</td></tr>` : ''}
    <tr><td class="grand">TOTAL</td><td class="amt grand">${money(due)}</td></tr>
  </table>
  ${(sale.payments ?? []).length ? `<hr><table>${paymentRows(sale)}</table>` : ''}
  ${opts.change !== undefined && opts.change > 0 ? `<hr><table><tr><td>Change</td><td class="amt">${money(opts.change)}</td></tr></table>` : ''}
  ${sale.note ? `<div class="footer">${esc(sale.note)}</div>` : ''}
  <div class="footer">Thank you. Please come again.</div>
</body></html>`;
}

/**
 * Open a print window for the receipt. Falls back to a hidden iframe when the
 * popup is blocked (both paths trigger the browser print dialog).
 */
export function printReceipt(sale: TradeSalesRow, storeName: string, opts: ReceiptOpts = {}): void {
  const html = renderReceiptHtml(sale, storeName, opts);
  const popup = window.open('', '_blank', 'width=420,height=640');
  if (popup) {
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    window.setTimeout(() => {
      try {
        popup.print();
      } catch {
        /* user may already have printed/closed */
      }
    }, 250);
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  window.setTimeout(() => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      window.setTimeout(() => iframe.remove(), 2000);
    }
  }, 250);
}
